import { useContext, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useCurrentUser } from 'context'
import { TeacherDashboardContext } from '../../context/TeacherDashboardContext'
import { useMediaQuery, useMantineTheme, useFeatureToggle, useArcTranslation } from 'hooks'
import { setDocumentTitle } from 'utils/documentTitle'
import { DashboardHeader, Stack, Button } from 'components'
import { arcHeroContentOffset } from 'components/ArcPageShell/constants'
import { IconUsersGroup } from '@tabler/icons-react'
import { TasksList } from '../TasksList'
import { GroupsList } from '../GroupsList'
import { ProjectsList } from '../ProjectsList'
import { ArcProjectsSection } from '../ArcProjectsSection'
import { ArcGroupsSection } from '../ArcGroupsSection'
import { OnboardingModal, useOnboarding } from '../OnboardingModal'
import { AddStudentsModal, AddStudentsModalProvider } from '../AddStudentsModal'
import { getCurrentTasks, getCurrentGroups, getCurrentProjects } from '../../service/teacherDashboard'
import { ErrorContext } from 'context'

/** Header actions component - navigates to Manage Students page */
const AddStudentsButton = () => {
  const { t } = useArcTranslation()
  const navigate = useNavigate()

  return (
    <Button
      variant='white'
      color='teal'
      leftIcon={<IconUsersGroup size={18} />}
      onClick={() => navigate('/teacher/manage-students')}
      radius='xl'
    >
      {t('manage_students.dashboard_button')}
    </Button>
  )
}

/** Dashboard content component */
const TeacherDashboardContent = () => {
  const { currentUser } = useCurrentUser()
  const theme = useMantineTheme()
  const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.sm})`)
  const { t } = useArcTranslation()
  const { setError } = useContext(ErrorContext)
  const { setTasks, setProjects, setGroups } = useContext(TeacherDashboardContext)
  const { showOnboarding, completeOnboarding } = useOnboarding()
  const isArcSignupEnabled = useFeatureToggle('arc-mosaico-project') as boolean

  const projectKind = [
    {
      value: 'draft',
      label: t('project_draft'),
    },
    {
      value: 'approved',
      label: t('project_progress'),
    },
  ]

  useEffect(() => {
    const loadDashBoardData = async () => {
      try {
        const tasksData = await getCurrentTasks()
        if (tasksData.status === 200) {
          setTasks(tasksData.data)
        } else {
          setError(tasksData.statusText)
        }

        const projectsData = await getCurrentProjects()
        if (projectsData.status === 200) {
          setProjects(projectsData.data)
        } else {
          setError(projectsData.statusText)
        }

        const groupsData = await getCurrentGroups()
        if (groupsData.status === 200) {
          const groups = groupsData.data
          setGroups(groups)
        } else {
          setError(groupsData.statusText)
        }
      } catch (error) {
        if (error instanceof Error) {
          setError(error.message)
        }
      }
    }
    loadDashBoardData()
    setDocumentTitle(t('new_teacher_dashboard.page_title'))
  }, [])

  return (
    <>
      {isArcSignupEnabled && <OnboardingModal opened={showOnboarding} onComplete={completeOnboarding} />}
      {isArcSignupEnabled && <AddStudentsModal />}
      <DashboardHeader
        name={currentUser?.full_name}
        isMobile={isMobile}
        actions={isArcSignupEnabled ? <AddStudentsButton /> : undefined}
      />
      {/* Arc: container top padding is the rhythm gap (not the legacy 80/100),
          so the hero clearance adds the difference back (AR-578) */}
      <Stack
        spacing='md'
        mt={isArcSignupEnabled ? arcHeroContentOffset(isMobile ? 0 : 120, isMobile) : isMobile ? 0 : 80}
      >
        {!isArcSignupEnabled && <TasksList />}
        {isArcSignupEnabled ? <ArcProjectsSection /> : <ProjectsList projectKind={projectKind} />}
        {isArcSignupEnabled ? <ArcGroupsSection /> : <GroupsList />}
      </Stack>
    </>
  )
}

/** Main component with Provider wrapper */
export const TeacherDashboard = () => {
  const isArcSignupEnabled = useFeatureToggle('arc-mosaico-project') as boolean
  const { setGroups } = useContext(TeacherDashboardContext)

  // Callback to refresh groups after students are added
  const handleStudentsAdded = useCallback(async () => {
    try {
      const groupsData = await getCurrentGroups()
      if (groupsData.status === 200) {
        setGroups(groupsData.data)
      }
    } catch (error: unknown) {
      // Log error but don't block - user already saw success message for student creation
      console.error('Failed to refresh groups:', error)
    }
  }, [setGroups])

  // Wrap with Provider only when feature is enabled
  if (isArcSignupEnabled) {
    return (
      <AddStudentsModalProvider onStudentsAdded={handleStudentsAdded}>
        <TeacherDashboardContent />
      </AddStudentsModalProvider>
    )
  }

  return <TeacherDashboardContent />
}
